/**
 * 🌠 Inner Light Wallet Pill — Header chip showing current Inner Light Crystals
 * + flashing +X delta when crystals go up (Wilds Lv 10+ drops).
 *
 * Polls /api/inner-light/state every 10s. When the balance increases vs last
 * poll, shows a cyan +X chip that fades after 4s. Cosmetic — no backend
 * changes required.
 *
 * Hidden until the user has earned at least one crystal (zero-state stays
 * out of the way until the system is relevant for them).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InnerLightWalletPill({ className = "" }) {
  const [balance, setBalance] = useState(null);
  const [unlocked, setUnlocked] = useState(null);
  const [delta, setDelta] = useState(0);
  const lastSeen = useRef(null);
  const deltaTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    const fetchNow = async () => {
      try {
        const r = await axios.get(`${API}/inner-light/state`);
        if (!alive) return;
        const next = Number.parseInt(r.data?.crystals) || 0;
        setUnlocked(!!r.data?.unlocked);
        if (lastSeen.current != null && next > lastSeen.current) {
          const diff = next - lastSeen.current;
          setDelta(diff);
          if (deltaTimer.current) clearTimeout(deltaTimer.current);
          deltaTimer.current = setTimeout(() => setDelta(0), 4000);
        }
        lastSeen.current = next;
        setBalance(next);
      } catch (e) { /* silent — pill hides on error */ }
    };
    fetchNow();
    const interval = setInterval(fetchNow, 10000);
    return () => {
      alive = false;
      clearInterval(interval);
      if (deltaTimer.current) clearTimeout(deltaTimer.current);
    };
  }, []);

  // 🔒 Hide until the entire Inner Light system unlocks (Pet Level 10 — owner
  // canon Feb 2026). Before that the pill stays invisible regardless of any
  // legacy balance the wallet might still carry.
  if (unlocked === null || unlocked === false) return null;
  // Even unlocked, hide if balance is 0 — keep the HUD clean
  if (balance == null || balance <= 0) return null;

  return (
    <div
      data-testid="header-inner-light-pill"
      className={`relative flex items-center gap-1 border border-cyan-900/60 px-2 py-1.5 bg-black/40 text-xs font-bold ${className}`}
      title="Inner Light Crystals — drop from Lv 10+ Wilds monsters"
    >
      <span className="text-base leading-none">🌠</span>
      <span className="tabular-nums text-cyan-200">{balance}</span>
      {delta > 0 && (
        <span
          data-testid="header-inner-light-delta"
          className="absolute -top-2 -right-2 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-cyan-400 text-black animate-pulse shadow-[0_0_8px_rgba(34,211,238,0.6)] leading-none"
        >
          +{delta}
        </span>
      )}
    </div>
  );
}
